/**
===================================================
Module: frontend.src.components.VerifyEmail.tsx
Description: Email verification component that handles verification links
Owner: Kawada Technosystem Co Ltd
All Rights Reserved
This script is proprietary and confidential.
Unauthorized copying, distribution, or modification of this
file, via any medium, is strictly prohibited.
===================================================
*/

import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useTranslation } from "react-i18next";
import "../styles/login.css";

/**
 * This component handles email verification links.
 * It automatically verifies the email using the code from the URL
 * and redirects to the login page on success.
 */
const VerifyEmail: React.FC = () => {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { confirmRegistration } = useAuth();
  const [status, setStatus] = useState<"verifying" | "success" | "error">("verifying");
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    const verifyEmail = async () => {
      const code = searchParams.get("code");
      const email = searchParams.get("email");

      if (!code || !email) {
        setStatus("error");
        setErrorMessage(t('verifyEmail.missingCodeOrEmail'));
        return;
      }

      try {
        await confirmRegistration(email, code);
        setStatus("success");
        // Redirect to login after 2 seconds
        setTimeout(() => {
          navigate("/login?message=verified", { replace: true });
        }, 2000);
      } catch (error: any) {
        setStatus("error");
        setErrorMessage(error.message || "Verification failed. Please try again.");
      }
    };

    verifyEmail();
  }, [searchParams, confirmRegistration, navigate]);

  return (
    <div className="login-container">
      <div className="login-card">
        <h2>{t('verifyEmail.title')}</h2>
        
        {status === "verifying" && (
          <div style={{ textAlign: "center", padding: "20px" }}>
            <div className="loading-spinner" style={{ margin: "0 auto 15px" }}></div>
            <p>{t('verifyEmail.verifying', 'Verifying your email...')}</p>
          </div>
        )}

        {status === "success" && (
          <div style={{ textAlign: "center", padding: "20px" }}>
            <div style={{ 
              fontSize: "48px", 
              color: "#28a745", 
              marginBottom: "15px" 
            }}>✓</div>
            <p style={{ color: "#28a745", fontWeight: "bold" }}>
              {t('verifyEmail.success')}
            </p>
            <p>{t('verifyEmail.redirecting')}</p>
          </div>
        )}

        {status === "error" && (
          <div style={{ textAlign: "center", padding: "20px" }}>
            <div style={{ 
              fontSize: "48px", 
              color: "#dc3545", 
              marginBottom: "15px" 
            }}>✗</div>
            <p style={{ color: "#dc3545", fontWeight: "bold" }}>
              {t('verifyEmail.error')}
            </p>
            <p>{errorMessage}</p>
            <button 
              onClick={() => navigate("/login")} 
              className="submit-btn"
              style={{ marginTop: "15px" }}
            >
              {t('verifyEmail.goToLogin')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default VerifyEmail;
